/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useCallback, useState } from 'react';

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	ActionIcon,
	Group,
} from '@mantine/core';

// Utils
import {
	getMintStatusLabel,
	getMintStatusType,
	handleOpenTransactionExternalLink,
	truncateString,
} from '@utils/functions';

// Components
import { MintStatus } from '@components/atoms/MintStatus';
import { Tooltip } from '@components/atoms/Tooltip';
import { Onglet } from '@components/atoms/Onglet';
import Table from './components/Table/Table';

// Style
import classes from './DrawerOffsetShowMore.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import OffsetLogo from '@assets/images/offset-logo.svg';

import { NOT_APPLICABLE } from '@config/constants/common';
import { useOffsetById } from '@features/offsets/api/get-offset-by-id';
import { TableAdditionalityProjects } from './components/TableAdditionalityProjects';

// Icons
import { IconExternalLink } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { Pill } from '@components/atoms/Pill';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerOffsetShowMore = (props: any) => {
	const { opened, handleclose, ...restProps } = props;
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ******************************** STATES ******************************* */
	const [activeTab, setActiveTab] = useState(
		'generalInformations'
	);

	const {
		name,
		brand,
		contributionDate,
		tags,
		quantity,
		startDate,
		endDate,
		mintStatus,
		blockchain,
		transactionHash,
	} = props.data;
	/* ******************************** HELPERS ******************************* */
	const handleCloseDrawerShowMore = useCallback(() => {
		setActiveTab('generalInformations');
		handleclose();
	}, []);

	const { additionalities, measurabilities } =
		useOffsetById(props.data.id);

	const { t, i18n } = useTranslation();

	/* ******************************** CONSTANTS ******************************* */

	const ongletItems = [
		{
			label: t('global.generalInformations'),
			value: 'generalInformations',
			id: 'id-generalInformations',
			nbr: null,
		},
		{
			label: t('global.details'),
			value: 'measurabilitiesProjects',
			id: '2',
			nbr: measurabilities ? measurabilities.length : 0,
		},
	];

	/* ****************************** RENDERING ****************************** */

	if (props.data)
		return (
			<NativeDrawer
				onClose={handleCloseDrawerShowMore}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={OffsetLogo}
						/>
					</Box>
					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{brand}
						</Text>
						{props.data.name && (
							<Text className={classes.customTitle}>
								{name}
							</Text>
						)}
					</Box>
				</Box>
				<Box className={classes.containerOnglet}>
					<Onglet
						items={ongletItems}
						onChange={(data: string | null) => {
							// eslint-disable-next-line @typescript-eslint/no-unused-expressions
							data !== null && setActiveTab(data);
						}}
					/>
				</Box>
				{/* Body */}
				{activeTab == 'generalInformations' && (
					<Box className={classes.bodyDrawer}>
						{name && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.name')}
								</Text>
								<Tooltip
									label={name}
									isActive={name.length > 20}
								>
									<Text className={classes.value}>
										{truncateString(name, 20)}
									</Text>
								</Tooltip>
							</Box>
						)}
						{brand && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.brand')}
								</Text>
								<Tooltip
									label={brand}
									isActive={brand.length > 20}
								>
									<Text className={classes.value}>
										{truncateString(brand, 20)}
									</Text>
								</Tooltip>
							</Box>
						)}
						{contributionDate && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.contributionDate')}
								</Text>
								<Text className={classes.value}>
									{contributionDate}
								</Text>
							</Box>
						)}

						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.tag')}
							</Text>
							{tags ? (
								<Group gap={'5px'}>
									{Array.isArray(tags) &&
										tags?.map((tag: any) => (
											<div key={tag.id}>
												<Pill
													color={tag.color}
													text={
														i18n.language == 'en'
															? tag?.value?.en ||
																NOT_APPLICABLE
															: tag?.value?.fr ||
																NOT_APPLICABLE
													}
												/>
											</div>
										))}
								</Group>
							) : (
								<Text className={classes.tag}>
									{NOT_APPLICABLE}
								</Text>
							)}
						</Box>

						{quantity && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.quantity')}
								</Text>
								<Text className={classes.value}>
									{quantity}
								</Text>
							</Box>
						)}
						{startDate && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.emissionStartDate')}
								</Text>
								<Text className={classes.value}>
									{startDate}
								</Text>
							</Box>
						)}
						{endDate && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.emissionEndDate')}
								</Text>
								<Text className={classes.value}>
									{endDate}
								</Text>
							</Box>
						)}
						{mintStatus && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.status')}
								</Text>
								<MintStatus
									type={getMintStatusType(mintStatus)}
									children={getMintStatusLabel(
										mintStatus
									)}
								/>
							</Box>
						)}

						{blockchain && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.blockchain')}
								</Text>
								<Text className={classes.value}>
									{blockchain}
								</Text>
							</Box>
						)}

						{transactionHash && (
							<Box className={classes.row}>
								<Group
									gap={isBigScreen ? 'xs' : '5px'}
									align="center"
									justify="center"
								>
									<Text className={classes.label}>
										{t('global.transactionId')}
									</Text>
									<ActionIcon
										onClick={() =>
											handleOpenTransactionExternalLink(
												transactionHash
											)
										}
										variant="transparent"
									>
										<IconExternalLink
											className={classes.label}
											size={'15px'}
										/>
									</ActionIcon>
								</Group>
								<Text
									onClick={() =>
										handleOpenTransactionExternalLink(
											transactionHash
										)
									}
									className={classes.transactionId}
								>
									{truncateString(
										transactionHash,
										isBigScreen ? 23 : 18
									)}
								</Text>
							</Box>
						)}
					</Box>
				)}
				{activeTab == 'measurabilitiesProjects' && (
					<Box className={classes.containerTabSecond}>
						<Box className={classes.row}>
							<Text className={classes.title}>
								{t('projectDetails.measurabilities')}
							</Text>
						</Box>
						<Box style={{ height: '20px' }} />
						{/* Mesurability projects */}
						<Table rows={measurabilities} />
						<Box style={{ height: '40px' }} />
						<Box className={classes.row}>
							<Text className={classes.title}>
								{t('projectDetails.additionalities')}
							</Text>
						</Box>
						<Box style={{ height: '20px' }} />
						{/* Additionality projects */}
						<TableAdditionalityProjects
							rows={additionalities}
						/>
					</Box>
				)}
			</NativeDrawer>
		);
};
